import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { AuthUser, Company } from '~/api/auth';
import { STORAGE_KEYS } from '~/storage-keys';

interface PersistedSession {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  companies: Company[];
}

interface AuthState extends PersistedSession {
  isHydrated: boolean;

  setTokens(payload: { accessToken: string; refreshToken: string }): void;
  setSession(payload: {
    accessToken: string;
    refreshToken: string;
    user: AuthUser;
    companies?: Company[];
  }): void;
  setProfile(payload: { user: AuthUser; companies: Company[] }): void;
  clearSession(): void;
}

const SIGNED_OUT: PersistedSession = {
  accessToken: null,
  refreshToken: null,
  user: null,
  companies: [],
};

// As empresas só chegam no login e ficam guardadas na sessão. A de antes da versão 1 veio do app anterior à virada de
// E11, com empresas sem `role` e `access`, e o app atual quebrava ao ler o nível de um módulo nelas. Uma sessão de
// versão anterior é descartada: a pessoa entra de novo, e o login traz as empresas no formato atual.
export const AUTH_STORAGE_VERSION = 1;

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...SIGNED_OUT,
      isHydrated: false,
      setTokens: ({ accessToken, refreshToken }) => set(() => ({ accessToken, refreshToken })),
      setSession: ({ accessToken, refreshToken, user, companies = [] }) =>
        set(() => ({ accessToken, refreshToken, user, companies })),
      setProfile: ({ user, companies }) => set(() => ({ user, companies })),
      clearSession: () => set(() => SIGNED_OUT),
    }),
    {
      name: STORAGE_KEYS.auth,
      storage: createJSONStorage(() => localStorage),
      version: AUTH_STORAGE_VERSION,
      migrate: () => SIGNED_OUT,
      partialize: (state): PersistedSession => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        companies: state.companies,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.isHydrated = true;
        }
      },
    },
  ),
);
